import type { ActionCreator, Dispatch, PayloadAction, PayloadActionCreator } from '@reduxjs/toolkit';
import { type RecordOf, isRecord } from 'immutable';
import identity from 'lodash/identity';
import omit from 'lodash/omit';
import { createAction as reduxActionsCreator } from 'redux-actions';
import { v4 as uuidv4 } from 'uuid';

import type { AxiosRequestConfig } from 'axios';
import type { BaseResource, QcsSyncResourcesConfig } from './resourceStoreTypes';

export type Meta<T extends BaseResource = BaseResource, R = any> = {
  context?: string;
  resolve?: (result: R) => void;
  reject?: (error: any) => void;
  undo?: any;
  response?: any;
  props?: any;
  config?: QcsSyncResourcesConfig<T>;
  axiosConfig?: AxiosRequestConfig;
  scope?: string;
};
// A helper to use with redux toolkit's createAction API to make an action creator that accepts payload and meta arguments.
export function getPayloadCreator<P = void, T extends BaseResource = BaseResource, M = Meta<T>>() {
  return (payload: P, meta?: M) => ({ payload, meta });
}

// enhances the default create action to pass along meta data from 2nd argument when using action
export const createAction = (type: string, payloadCreator = identity, metaCreator = (_: any, meta: any) => meta) =>
  reduxActionsCreator(type, payloadCreator, metaCreator);

// ensure clientId is set
export const payloadCreate = <T extends { clientId?: string }>(payload: RecordOf<T> | T) => {
  if (!payload.clientId) {
    const clientId = uuidv4().toUpperCase();
    return 'merge' in payload ? payload.merge({ clientId } as T) : { ...payload, clientId };
  }
  return payload;
};

// ensure isDeleted=true
export const payloadDelete = <T extends { isDeleted?: boolean }>(payload: RecordOf<T> | T) =>
  isRecord(payload) ? payload.merge({ isDeleted: true } as T) : { ...payload, isDeleted: true };

export const bindPromiseAction =
  <P = any, M = any, R = void>(
    dispatch: Dispatch<PayloadAction<P, string, M & { resolve: (result: R) => void; reject: (e: any) => void }>>,
    actionCreator: PayloadActionCreator<P, string> | ActionCreator<any>,
  ) =>
  (payload?: P, meta?: M): Promise<R> =>
    new Promise<R>((resolve, reject) => dispatch(actionCreator(payload, { ...meta, resolve, reject })));

const blacklistForCpMetaForResponseAction = ['resolve', 'reject'];
const blacklistForCpMetaForEmbeddedAction = ['resolve', 'reject'];

export const cpMetaForResponseAction = (meta: any) => (meta ? omit(meta, blacklistForCpMetaForResponseAction) : null);

export const cpMetaForEmbeddedAction = (meta: any) => (meta ? omit(meta, blacklistForCpMetaForEmbeddedAction) : null);
